import { type RefObject, useEffect } from 'react';

import { useNavigationGuard } from './NavigationGuardContext';

/**
 * While `hasUnsavedChanges`, every in-app navigation and a browser unload waits behind the
 * Save / Discard / Cancel prompt. The prompt's Save is the editor's own `save()`, so the editor
 * stays the single owner of what saving means; Discard needs no work because leaving unmounts the editor.
 */
export const useEditorNavigationGuard = (
  hasUnsavedChanges: boolean,
  editorRef: RefObject<{ save: () => boolean } | null>,
): void => {
  const { setNavigationGuard } = useNavigationGuard();
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    setNavigationGuard({
      hasChanges: true,
      saveChanges: () => editorRef.current?.save() ?? true,
      discardChanges: () => {},
    });
    return () => setNavigationGuard(null);
  }, [hasUnsavedChanges, editorRef, setNavigationGuard]);
};
